import { Group, Button, ActionIcon, Tooltip } from '@mantine/core';
import { IconBrandTwitter, IconBrandFacebook, IconBrandLinkedin, IconLink } from '@tabler/icons-react';
import { useTranslation } from '../../contexts/hooks';

interface SocialShareProps {
  url?: string;
  title?: string;
  description?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'buttons' | 'icons';
}

export function SocialShare({ 
  url = window.location.href, 
  title = 'PDF Form Editor - Create Interactive PDF Forms Online',
  size = 'sm',
  variant = 'icons'
}: SocialShareProps) {
  const { t } = useTranslation();
  const encodedUrl = encodeURIComponent(url);
  const encodedTitle = encodeURIComponent(title);

  const shareLinks = {
    twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(url);
      // You might want to show a notification here
    } catch (err) {
      console.error('Failed to copy URL: ', err);
    }
  };

  const openShare = (platform: keyof typeof shareLinks) => {
    window.open(shareLinks[platform], '_blank', 'noopener,noreferrer,width=600,height=400');
  };

  if (variant === 'buttons') {
    return (
      <Group gap="xs">
        <Button
          size={size}
          variant="light"
          leftSection={<IconBrandTwitter size={16} />}
          onClick={() => openShare('twitter')}
        >
          {t('social.platform.twitter')}
        </Button>
        <Button
          size={size}
          variant="light"
          leftSection={<IconBrandFacebook size={16} />}
          onClick={() => openShare('facebook')}
        >
          {t('social.platform.facebook')}
        </Button>
        <Button
          size={size}
          variant="light"
          leftSection={<IconBrandLinkedin size={16} />}
          onClick={() => openShare('linkedin')}
        >
          {t('social.platform.linkedin')}
        </Button>
        <Button
          size={size}
          variant="light"
          leftSection={<IconLink size={16} />}
          onClick={copyToClipboard}
        >
          {t('social.share.copy')}
        </Button>
      </Group>
    );
  }

  return (
    <Group gap="xs">
      <Tooltip label={t('social.share.twitter')}>
        <ActionIcon
          size={size}
          variant="light"
          color="blue"
          onClick={() => openShare('twitter')}
        >
          <IconBrandTwitter size={16} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label={t('social.share.facebook')}>
        <ActionIcon
          size={size}
          variant="light"
          color="blue"
          onClick={() => openShare('facebook')}
        >
          <IconBrandFacebook size={16} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label={t('social.share.linkedin')}>
        <ActionIcon
          size={size}
          variant="light"
          color="blue"
          onClick={() => openShare('linkedin')}
        >
          <IconBrandLinkedin size={16} />
        </ActionIcon>
      </Tooltip>
      <Tooltip label={t('social.share.copy')}>
        <ActionIcon
          size={size}
          variant="light"
          color="gray"
          onClick={copyToClipboard}
        >
          <IconLink size={16} />
        </ActionIcon>
      </Tooltip>
    </Group>
  );
}